import React, { useRef, useState } from 'react';
import { type FieldErrorProps as RACFieldErrorProps, FieldError as RACFieldError, composeRenderProps, Button, Dialog } from 'react-aria-components';
import { InfoIcon } from 'lucide-react';
import { cn } from '~/shared/components/utils';
import { Popover } from '~/shared/components/Popover';
import { fieldMessageStyles } from './util';

interface FieldErrorProps extends Omit<RACFieldErrorProps, 'children'> {
	errorDescription?: string;
	errorCode?: string;
	errorMessage: string;
}

export const FieldError = React.memo(function FieldErrorComponent({ errorDescription, errorCode, ...props }: FieldErrorProps) {
	return (
		<RACFieldError
			{...props}
			className={composeRenderProps(props.className, className => cn(fieldMessageStyles({ intent: 'error' }), className))}
		>
			{props.errorMessage}
			{errorDescription && <ErrorDescriptionPopover description={errorDescription} errorCode={errorCode} />}
		</RACFieldError>
	);
});

export const FieldErrorWithoutAria = React.memo(function FieldErrorWithoutAriaComponent({
	errorDescription,
	errorCode,
	errorMessage,
	className
}: FieldErrorProps & { className?: string }) {
	return (
		<div className={cn(fieldMessageStyles({ intent: 'error' }), className)}>
			{errorMessage}
			{errorDescription && <ErrorDescriptionPopover description={errorDescription} errorCode={errorCode} />}
		</div>
	);
});

function ErrorDescriptionPopover({ description, errorCode }: { description: string; errorCode?: string }) {
	const triggerRef = useRef<HTMLButtonElement>(null);
	const [isOpen, setIsOpen] = useState(false);

	return (
		<>
			<Button
				ref={triggerRef}
				aria-label="Error details"
				onPress={() => setIsOpen(v => !v)}
				className="inline-flex shrink-0 items-center text-red-600 hover:text-red-800"
			>
				<InfoIcon className="size-4" />
			</Button>
			<Popover triggerRef={triggerRef} isOpen={isOpen} onOpenChange={setIsOpen} showArrow className="max-w-xs p-3">
				<Dialog aria-label="Error description" className="text-sm outline-none">
					{errorCode && (
						<span className="mb-1.5 inline-block rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">{errorCode}</span>
					)}
					<p>{description}</p>
				</Dialog>
			</Popover>
		</>
	);
}
